import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { MessageSquareText, Smartphone, Mail, Bell, RefreshCw, Loader2, CheckCircle2, FlaskConical } from "lucide-react";

const CHANNELS = [
  { key: "", label: "All" },
  { key: "sms", label: "SMS" },
  { key: "push", label: "Push" },
  { key: "email", label: "Email" },
];

const CH_META = {
  sms: { icon: Smartphone, cls: "bg-emerald-50 text-emerald-600", label: "SMS · Twilio" },
  push: { icon: Bell, cls: "bg-cobalt-light text-cobalt", label: "Push · Firebase" },
  email: { icon: Mail, cls: "bg-amber-50 text-amber-600", label: "Email · Resend" },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
};

export default function AdminNotificationsFeed() {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [channel, setChannel] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [st, box] = await Promise.all([
        api.get("/admin/integrations/status"),
        api.get("/admin/dev-outbox", { params: { limit: 100, ...(channel ? { channel } : {}) } }),
      ]);
      setStatus(st.data);
      setItems(box.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [channel]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const ProviderBadge = ({ name, info }) => {
    const live = info?.live;
    return (
      <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm" data-testid={`provider-status-${name}`}>
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400">{name}</div>
          <div className="font-heading font-bold text-navy capitalize">{info?.provider}</div>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${live ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
          {live ? <><CheckCircle2 className="w-3.5 h-3.5" /> LIVE</> : <><FlaskConical className="w-3.5 h-3.5" /> MOCK</>}
        </span>
      </div>
    );
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-notifications-feed">
      <div className="rounded-2xl bg-amber-50 border border-amber-100 p-3 text-xs text-amber-700">
        Outbound alerts are captured here in <strong>MOCK</strong> mode until production keys are added. Each provider flips to <strong>LIVE</strong> automatically once its credentials are set — no code changes.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ProviderBadge name="SMS" info={status?.sms} />
        <ProviderBadge name="Push" info={status?.push} />
        <ProviderBadge name="Email" info={status?.email} />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {CHANNELS.map((c) => (
            <button key={c.key} onClick={() => setChannel(c.key)} data-testid={`notif-filter-${c.key || "all"}`}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all ${channel === c.key ? "bg-navy text-white border-navy" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {c.label}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="notif-refresh" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-navy text-sm font-medium hover:border-cobalt hover:text-cobalt">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-50" data-testid="notif-list">
        {items.length === 0 ? (
          <div className="p-10 text-center text-slate-400">
            <MessageSquareText className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No captured alerts yet. Trigger an RN dispatch, hot lead, or compliance email to see them here.
          </div>
        ) : items.map((n, i) => {
          const meta = CH_META[n.channel] || { icon: Bell, cls: "bg-slate-100 text-slate-500", label: n.channel };
          const Icon = meta.icon;
          const to = Array.isArray(n.to) ? n.to.join(", ") : n.to;
          return (
            <div key={i} className="flex items-start gap-3 p-4" data-testid={`notif-item-${i}`}>
              <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${meta.cls}`}><Icon className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                  {n.subject && <span className="font-semibold text-navy text-sm truncate">{n.subject}</span>}
                  <span className="ml-auto text-xs text-slate-300">{timeAgo(n.created_at)}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">To: <span className="font-mono">{to}</span></div>
                <div className="text-sm text-slate-600 mt-1 line-clamp-2">{stripHtml(n.body)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function stripHtml(s) {
  if (!s) return "";
  const t = String(s).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return t.length > 220 ? t.slice(0, 220) + "…" : t;
}
